/**
 * The password policy, once (UC-P01 PG-02; TCH-02-FR-03).
 *
 * There were four copies and none of them was the governed rule. UC-T01's
 * canonical baseline states it: a minimum of 15 characters for single-factor
 * use, no composition rules, a maximum accepted length of at least 64, spaces
 * and passphrases permitted, and a blocklist of compromised and common
 * passwords (parameters `password.minLength` 15 and `password.maxLength` 64,
 * owned by CTO / Security). The P01 gap register's treatment for PG-02 is
 * "use shared Auth policy; remove conflicting frontend composition logic", and
 * its NFR puts password thresholds in governed shared configuration.
 *
 * What the code had instead: Cognito asked for 8 characters with upper case,
 * lower case and a digit; the registration DTO, the reset DTO and the form
 * each also demanded one of six symbols and refused every other character.
 *
 * The blocklist itself lives in the backend (identity-access): GAP-T01-15 was
 * closed by the CTO on 12 Oct 2026 — Have I Been Pwned's k-anonymity range API
 * plus a bundled list — and both need a server (one calls out to HIBP, the
 * other should not ship to every browser). What is shared is how a refusal
 * reads, below, so the API and the form say the same thing.
 *
 * The Cognito user pool is configured from the same values; the infrastructure
 * stack cannot import this package, so a backend gate reads the stack and
 * fails if the two diverge.
 */
export const PASSWORD_POLICY = {
  minLength: 15,
  maxLength: 64,
  requireLowercase: false,
  requireUppercase: false,
  requireDigits: false,
  requireSymbols: false,
} as const;

/**
 * The policy as a pattern, for validators that take one. Length only: no
 * composition rule, and any character — a space, an accent, a symbol — is
 * accepted, because a passphrase is exactly what the policy asks people for.
 */
export const PASSWORD_PATTERN = new RegExp(
  `^[\\s\\S]{${PASSWORD_POLICY.minLength},${PASSWORD_POLICY.maxLength}}$`,
);

/** What the policy asks for, in the words a person filling in the form reads. */
export const PASSWORD_POLICY_MESSAGE_ES =
  `Entre ${PASSWORD_POLICY.minLength} y ${PASSWORD_POLICY.maxLength} caracteres. ` +
  'Puedes usar una frase con espacios; no hace falta mezclar mayúsculas, números ni símbolos.';

/**
 * Machine code for a password refused by the blocklist (TCH-02-FR-03,
 * GAP-T01-15). The form keys on this, not on the wording, to put the message
 * on the password field.
 */
export const PASSWORD_BLOCKLISTED_CODE = 'PASSWORD_BLOCKLISTED';

/**
 * One message whichever source matched — the breach corpus or the bundled
 * list. Saying which would tell an attacker which list a guess is on, and the
 * person choosing a password needs to know only that this one will not do.
 */
export const PASSWORD_BLOCKLISTED_MESSAGE_ES =
  'Esta contraseña es demasiado común o apareció en una filtración de datos. ' +
  'Elige otra; una frase de varias palabras que no uses en otros sitios funciona bien.';
